import { useState, useEffect, useCallback } from "react"
import {
  IconCash,
  IconLoader2,
  IconPlus,
  IconChevronDown,
  IconChevronRight,
  IconCheck,
  IconX,
} from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { api } from "@/lib/api"

// ─── Types ────────────────────────────────────────────────────────────────────

interface FundingSourceSummary {
  id: number
  name: string
  contractNumber: string | null
  description: string | null
  isActive: boolean
  totalBudget: number
  totalDisbursed: number
  remaining: number
  programs: string[]
}

interface FundingSourceDetail extends Omit<FundingSourceSummary, "programs"> {
  budgetTransactions: BudgetTransaction[]
  programs: FundingSourceProgram[]
}

interface FundingSourceProgram {
  benefitProgramId: number
  name: string
}

interface BudgetTransaction {
  id: number
  transactionType: string
  amount: number
  effectiveDate: string
  description: string | null
  createdAt: string
  createdBy: string
}

interface AddBudgetTransactionRequest {
  transactionType: string
  amount: number
  effectiveDate: string
  description?: string
}

interface CreateFundingSourceRequest {
  name: string
  contractNumber?: string
  description?: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

function formatDate(iso: string): string {
  return new Date(iso + (iso.includes("T") ? "" : "T00:00:00")).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

const transactionTypeLabels: Record<string, string> = {
  Original: "Original",
  Amendment: "Amendment",
  CarryOver: "Carry-over",
  Transfer: "Transfer",
}

function TransactionTypeBadge({ type }: { type: string }) {
  const label = transactionTypeLabels[type] ?? type
  return <Badge variant="outline" className="text-xs">{label}</Badge>
}

function BudgetBar({ disbursed, total }: { disbursed: number; total: number }) {
  if (total === 0) {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  const pct = Math.min(100, (disbursed / total) * 100)
  const isOverBudget = disbursed > total
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${isOverBudget ? "bg-red-500" : pct > 80 ? "bg-amber-500" : "bg-green-500"}`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground">{pct.toFixed(0)}%</span>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function FundingSourcesPage() {
  const [sources, setSources] = useState<FundingSourceSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [expandedDetail, setExpandedDetail] = useState<FundingSourceDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [includeInactive, setIncludeInactive] = useState(false)

  const loadSources = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await api.get<FundingSourceSummary[]>(
        `/api/funding-sources?includeInactive=${includeInactive}`
      )
      setSources(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load funding sources")
    } finally {
      setLoading(false)
    }
  }, [includeInactive])

  useEffect(() => {
    loadSources()
  }, [loadSources])

  const toggleExpand = async (id: number) => {
    if (expandedId === id) {
      setExpandedId(null)
      setExpandedDetail(null)
      return
    }
    setExpandedId(id)
    setDetailLoading(true)
    try {
      const detail = await api.get<FundingSourceDetail>(`/api/funding-sources/${id}`)
      setExpandedDetail(detail)
    } catch {
      setExpandedDetail(null)
    } finally {
      setDetailLoading(false)
    }
  }

  const handleTransactionAdded = async () => {
    if (expandedId) {
      const detail = await api.get<FundingSourceDetail>(`/api/funding-sources/${expandedId}`)
      setExpandedDetail(detail)
    }
    loadSources()
  }

  // Summary cards — only include sources that have a budget set
  const sourcesWithBudget = sources.filter(s => s.totalBudget > 0)
  const totalBudget = sourcesWithBudget.reduce((sum, s) => sum + s.totalBudget, 0)
  const totalDisbursed = sourcesWithBudget.reduce((sum, s) => sum + s.totalDisbursed, 0)
  const totalRemaining = sourcesWithBudget.reduce((sum, s) => sum + s.remaining, 0)
  const hasBudgets = sourcesWithBudget.length > 0

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <IconCash className="size-6" />
          <h1 className="text-2xl font-bold">Funding Sources</h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="rounded"
            />
            Show inactive
          </label>
          <CreateFundingSourceDialog onCreated={loadSources} />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Budget</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? <Skeleton className="h-8 w-32" /> : hasBudgets ? formatCurrency(totalBudget) : <span className="text-muted-foreground">Not set</span>}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Disbursed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? <Skeleton className="h-8 w-32" /> : hasBudgets ? formatCurrency(totalDisbursed) : <span className="text-muted-foreground">—</span>}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Remaining</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? <Skeleton className="h-8 w-32" /> : hasBudgets ? formatCurrency(totalRemaining) : <span className="text-muted-foreground">—</span>}</div>
          </CardContent>
        </Card>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Name</TableHead>
              <TableHead>Contract</TableHead>
              <TableHead>Programs</TableHead>
              <TableHead className="text-right">Budget</TableHead>
              <TableHead className="text-right">Disbursed</TableHead>
              <TableHead className="text-right">Remaining</TableHead>
              <TableHead>Utilization</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 9 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : sources.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  No funding sources found.
                </TableCell>
              </TableRow>
            ) : (
              sources.map((source) => (
                <>
                  <TableRow
                    key={source.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => toggleExpand(source.id)}
                  >
                    <TableCell>
                      {expandedId === source.id
                        ? <IconChevronDown className="size-4" />
                        : <IconChevronRight className="size-4" />
                      }
                    </TableCell>
                    <TableCell className="font-medium">{source.name}</TableCell>
                    <TableCell className="font-mono text-muted-foreground text-sm">{source.contractNumber ?? "—"}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {source.programs.map((p) => (
                          <Badge key={p} variant="secondary" className="text-xs">{p}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">{source.totalBudget === 0 ? <span className="text-muted-foreground">Not set</span> : formatCurrency(source.totalBudget)}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(source.totalDisbursed)}</TableCell>
                    <TableCell className="text-right font-mono">{source.totalBudget === 0 ? <span className="text-muted-foreground">—</span> : formatCurrency(source.remaining)}</TableCell>
                    <TableCell>
                      <BudgetBar disbursed={source.totalDisbursed} total={source.totalBudget} />
                    </TableCell>
                  </TableRow>
                  {expandedId === source.id && (
                    <TableRow key={`${source.id}-detail`}>
                      <TableCell colSpan={8} className="bg-muted/30 p-0">
                        <ExpandedDetail
                          detail={expandedDetail}
                          loading={detailLoading}
                          fundingSourceId={source.id}
                          onTransactionAdded={handleTransactionAdded}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

// ─── Expanded Detail ──────────────────────────────────────────────────────────

function ExpandedDetail({
  detail,
  loading,
  fundingSourceId,
  onTransactionAdded,
}: {
  detail: FundingSourceDetail | null
  loading: boolean
  fundingSourceId: number
  onTransactionAdded: () => void
}) {
  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <IconLoader2 className="size-4 animate-spin" />
        Loading details…
      </div>
    )
  }

  if (!detail) {
    return (
      <div className="p-6 text-muted-foreground">Failed to load details.</div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Budget Transactions</h3>
        <AddTransactionDialog
          fundingSourceId={fundingSourceId}
          onAdded={onTransactionAdded}
        />
      </div>
      {detail.budgetTransactions.length === 0 ? (
        <p className="text-sm text-muted-foreground">No budget transactions recorded yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Added</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {detail.budgetTransactions.map((tx, index) => {
              const isFirst = index === 0
              const amountColor = isFirst
                ? ""
                : tx.amount >= 0
                  ? "text-green-600 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              const prefix = isFirst ? "" : tx.amount >= 0 ? "+" : ""
              return (
                <TableRow key={tx.id}>
                  <TableCell><TransactionTypeBadge type={tx.transactionType} /></TableCell>
                  <TableCell>{formatDate(tx.effectiveDate)}</TableCell>
                  <TableCell className="text-muted-foreground">{tx.description ?? "—"}</TableCell>
                  <TableCell className={`text-right font-mono ${amountColor}`}>{prefix}{formatCurrency(tx.amount)}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{formatDate(tx.createdAt)}</TableCell>
                </TableRow>
              )
            })}
            <TableRow className="font-semibold">
              <TableCell colSpan={3}>Total Budget</TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(detail.budgetTransactions.reduce((sum, tx) => sum + tx.amount, 0))}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      )}
    </div>
  )
}

// ─── Add Transaction Dialog ───────────────────────────────────────────────────

function AddTransactionDialog({
  fundingSourceId,
  onAdded,
}: {
  fundingSourceId: number
  onAdded: () => void
}) {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [type, setType] = useState("Original")
  const [amount, setAmount] = useState("")
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [description, setDescription] = useState("")

  const handleSubmit = async () => {
    if (!amount || !date) return
    setSaving(true)
    try {
      await api.post<BudgetTransaction>(
        `/api/funding-sources/${fundingSourceId}/transactions`,
        {
          transactionType: type,
          amount: parseFloat(amount),
          effectiveDate: date,
          description: description || undefined,
        } satisfies AddBudgetTransactionRequest
      )
      setOpen(false)
      setAmount("")
      setDescription("")
      onAdded()
    } catch {
      // error handling could be improved
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <IconPlus className="size-4 mr-1" />
          Add Transaction
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Budget Transaction</DialogTitle>
          <DialogDescription>
            Record a budget change for this funding source.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Transaction Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Original">Original</SelectItem>
                <SelectItem value="Amendment">Amendment</SelectItem>
                <SelectItem value="CarryOver">Carry-over</SelectItem>
                <SelectItem value="Transfer">Transfer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Amount</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label>Effective Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label>Description (optional)</Label>
            <Input
              placeholder="e.g., Amendment B"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving || !amount || !date}>
            {saving && <IconLoader2 className="size-4 mr-1 animate-spin" />}
            Add Transaction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Create Funding Source Dialog ─────────────────────────────────────────────

function CreateFundingSourceDialog({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState("")
  const [contractNumber, setContractNumber] = useState("")
  const [description, setDescription] = useState("")

  const handleSubmit = async () => {
    if (!name) return
    setSaving(true)
    try {
      await api.post<FundingSourceDetail>(
        "/api/funding-sources",
        {
          name,
          contractNumber: contractNumber || undefined,
          description: description || undefined,
        } satisfies CreateFundingSourceRequest
      )
      setOpen(false)
      setName("")
      setContractNumber("")
      setDescription("")
      onCreated()
    } catch {
      // error handling could be improved
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <IconPlus className="size-4 mr-1" />
          New Funding Source
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Funding Source</DialogTitle>
          <DialogDescription>
            Add a new funding source to track.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Name</Label>
            <Input
              placeholder="e.g., PY25 Regular LIHEAP"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label>Contract Number (optional)</Label>
            <Input
              placeholder="e.g., 25-32606"
              value={contractNumber}
              onChange={(e) => setContractNumber(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label>Description (optional)</Label>
            <Input
              placeholder="e.g., Regular LIHEAP"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving || !name}>
            {saving && <IconLoader2 className="size-4 mr-1 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
